import { useCallback, useSyncExternalStore } from "react";

import { customIndustryAgentRunService } from "../services/customIndustryAgentRun/customIndustryAgentRunService";
import type { ClassifiedDomain, CreatedIndustry, CustomIndustryAgentState } from "./customIndustryAgent.types";

export type { ClassifiedDomain, CreatedIndustry, CustomIndustryAgentState };
export { AGENT_STEPS } from "./customIndustryAgent.types";

type UseCustomIndustryAgentReturn = {
    state: CustomIndustryAgentState;
    completedSteps: number;
    discoveredDomains: ClassifiedDomain[] | null;
    topicSummary: string | null;
    trafficFailures: string[] | null;
    createdSegments: ClassifiedDomain[] | null;
    rulesGenerated: number;
    maxSegments: number;
    segmentsOverBudget: number;
    industry: CreatedIndustry | null;
    errorCode: string | null;
    isRunBlocking: boolean;
    description: string;
    countryId: number | null;
    generate: (countryId: number, description: string, maxSegments?: number) => Promise<void>;
    cancel: () => void;
    dismiss: () => void;
    dismissError: () => void;
    reportHandoffFailure: () => void;
};

export const useCustomIndustryAgent = (): UseCustomIndustryAgentReturn => {
    const snapshot = useSyncExternalStore(
        customIndustryAgentRunService.subscribe,
        customIndustryAgentRunService.getSnapshot,
    );

    const generate = useCallback(
        (countryId: number, description: string, maxSegments?: number) =>
            customIndustryAgentRunService.generate(countryId, description, maxSegments),
        [],
    );

    const cancel = useCallback(() => customIndustryAgentRunService.cancel(), []);

    const dismiss = useCallback(() => customIndustryAgentRunService.dismiss(), []);

    const dismissError = useCallback(() => customIndustryAgentRunService.dismissError(), []);

    const reportHandoffFailure = useCallback(() => customIndustryAgentRunService.reportHandoffFailure(), []);

    return {
        state: snapshot.status,
        completedSteps: snapshot.completedSteps,
        discoveredDomains: snapshot.discoveredDomains,
        topicSummary: snapshot.topicSummary,
        trafficFailures: snapshot.trafficFailures,
        createdSegments: snapshot.createdSegments,
        rulesGenerated: snapshot.rulesGenerated,
        maxSegments: snapshot.maxSegments,
        segmentsOverBudget: snapshot.segmentsOverBudget,
        industry: snapshot.industry,
        errorCode: snapshot.errorCode,
        isRunBlocking: customIndustryAgentRunService.isRunBlockingNewStart(),
        description: snapshot.description,
        countryId: snapshot.countryId,
        generate,
        cancel,
        dismiss,
        dismissError,
        reportHandoffFailure,
    };
};
